import * as React from "react";
import { observer, inject } from "mobx-react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faPhone } from "@fortawesome/free-solid-svg-icons";

import { Contact, Business } from "src/model/Customer";

import { CustomerStore } from "../../stores/CustomerStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import BusinessContactRelationshipPanel from "./BusinessContactRelationshipPanel";
import { successMessage, errorMessage } from "../common/Toast";

const NONE_ID: number = -1;

export interface Props {
    customerStore?: CustomerStore;
    configurationStore?: ConfigurationStore;
}

export interface EditableFields {
    // Contact specific
    firstName: string;
    lastName: string;
    middleName: string;

    // Business specific
    businessName: string;

    // Common
    address: string;
    countryId: number;
    provinceId: number;
    city: string;
    postalCode: string;
    email: string;
    faxNumber: string;
    notes: string;
    primaryPhone: string;
    secondaryPhone: string;
    identificationTypeId: number;
    identificationNumber: string;
    firstNationsIdNumber: string;
}
export interface ComponentState {
    isEditable: boolean;
    isLinkingContact: boolean;
    wasValidated: boolean;
}
type State = ComponentState & EditableFields;

export interface OptionType {
    value: number;
    label: string;
}

@inject("customerStore")
@inject("configurationStore")
@observer
export default class BasicInformation extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            isLinkingContact: true,
            wasValidated: false,

            address: "",
            countryId: NONE_ID,
            provinceId: NONE_ID,
            city: "",
            postalCode: "",
            faxNumber: "",
            firstName: "",
            lastName: "",
            middleName: "",
            email: "",
            notes: "",
            primaryPhone: "",
            secondaryPhone: "",
            businessName: "",
            identificationTypeId: NONE_ID,
            identificationNumber: "",
            firstNationsIdNumber: ""
        };
    }

    editFieds = () => {
        //this.setState({ isEditable: true });
        this.setState(this.copyCustomerToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        this.props
            .customerStore!.updateFieldsByObject(this.buildFieldsToUpdate())
            .then(() => {
                this.setState({ isEditable: false });
                successMessage("Changes successfully saved.");
            })
            .catch(error => errorMessage(error));
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    onSelectProvince = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        e.preventDefault();
        const provinceId = parseInt(e.target.value, 10);
        this.setState({ provinceId });
    };

    onSelectCountry = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        e.preventDefault();
        const countryId = parseInt(e.target.value, 10);
        this.setState({ countryId, provinceId: NONE_ID });
    };

    onSelectIdentification = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const identificationTypeId = parseInt(e.target.value, 10);
        this.setState({ identificationTypeId });
    };

    handleOnChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    submit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (this.formValidation()) {
            this.setState({ wasValidated: false });
            this.saveEditableFields();
        } else {
            this.setState({ wasValidated: true, isEditable: true });
        }
    };

    formValidation = (): boolean => {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return false;
        }

        if (customer.type === "BUSINESS") {
            return this.state.businessName === "" ? false : true;
        } else {
            return this.state.firstName === "" || this.state.lastName === "" ? false : true;
        }
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const { configurationStore } = this.props;
        if (!configurationStore) {
            throw new Error(" ** No Configuration Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return <div />;
        }

        return (
            <div>
                <div className={`card shadow edit-card ${this.state.isEditable ? "editable" : "read-only"}`}>
                    <div className="card-header">
                        <div className="row mx-0">
                            <div className="col-8">Basic Information</div>
                            <div className="col-4 text-right pr-0 mr-0">
                                {!this.state.isEditable && (
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        data-toggle="button"
                                        aria-pressed="false"
                                        onClick={this.editFieds}
                                    >
                                        Edit Basic Information
                                    </button>
                                )}
                                {this.state.isEditable && (
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm mr-2"
                                            data-toggle="button"
                                            aria-pressed="false"
                                            onClick={this.cancelEditableFields}
                                        >
                                            {" "}
                                            <FontAwesomeIcon icon={faBan} className="mr-2" />
                                            Cancel
                                        </button>
                                        <button
                                            form="customer-form"
                                            type="submit"
                                            className="btn btn-primary btn-sm px-3"
                                            data-toggle="button"
                                            aria-pressed="false"
                                        >
                                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                                            Save
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <form
                            id="customer-form"
                            className={"needs-validation" + (this.state.wasValidated ? " was-validated" : "")}
                            onSubmit={e => this.submit(e)}
                            noValidate={true}
                        >
                            {customer.type === "CONTACT" && (
                                <div className="form-row mb-3">
                                    <div className="form-group col-md-4">
                                        <label htmlFor="firstName">
                                            First Name: <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="form-control"
                                            pattern="^[a-zA-Z ]*$"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.firstName : (customer as Contact).firstName}
                                            required={true}
                                            autoFocus={true}
                                            readOnly={!this.state.isEditable}
                                        />
                                        <div className="invalid-feedback">Please provide a customer first name.</div>
                                    </div>
                                    <div className="form-group col-md-4">
                                        <label htmlFor="middleName">Middle Name: </label>
                                        <input
                                            type="text"
                                            name="middleName"
                                            className="form-control not-required"
                                            pattern="^[a-zA-Z ]*$"
                                            onChange={this.handleOnChange}
                                            placeholder={this.state.isEditable ? "" : "-"}
                                            value={this.state.isEditable ? this.state.middleName : (customer as Contact).middleName}
                                            readOnly={!this.state.isEditable}
                                        />
                                    </div>
                                    <div className="form-group col-md-4">
                                        <label htmlFor="lastName">
                                            Last Name: <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            className="form-control"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.lastName : (customer as Contact).lastName}
                                            readOnly={!this.state.isEditable}
                                            required={true}
                                        />
                                        <div className="invalid-feedback">Please provide a customer last name.</div>
                                    </div>
                                </div>
                            )}
                            {customer.type === "BUSINESS" && (
                                <div className="form-row mb-3">
                                    <div className="form-group col-md-12">
                                        <label htmlFor="firstName">
                                            Business Name: <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="businessName"
                                            className="form-control"
                                            pattern="^[a-zA-Z ]*$"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.businessName : (customer as Business).businessName}
                                            readOnly={!this.state.isEditable}
                                            required={true}
                                        />
                                        <div className="invalid-feedback">Please provide a business name.</div>
                                    </div>
                                </div>
                            )}
                            <div className="form-row mb-3">
                                <div className="form-group col-md-8">
                                    <label htmlFor="address">Street Address: </label>
                                    <input
                                        type="text"
                                        name="address"
                                        className="form-control not-required"
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        onChange={this.handleOnChange}
                                        value={this.state.isEditable ? this.state.address : customer.address ? customer.address.address : ""}
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="city">City: </label>
                                    <input
                                        type="text"
                                        name="city"
                                        className="form-control not-required"
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        onChange={this.handleOnChange}
                                        value={this.state.isEditable ? this.state.city : customer.address ? customer.address.city : ""}
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div className="form-group col-md-4">
                                    <label htmlFor="postalCode">Postal Code: </label>
                                    <input
                                        type="text"
                                        name="postalCode"
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        className="form-control not-required"
                                        onChange={this.handleOnChange}
                                        value={this.state.isEditable ? this.state.postalCode : customer.address ? customer.address.postalCode : ""}
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="country">Country: </label>
                                    <select
                                        className="form-control not-required"
                                        name="country"
                                        disabled={!this.state.isEditable}
                                        onChange={this.onSelectCountry}
                                        value={
                                            this.state.isEditable
                                                ? this.state.countryId
                                                : (customer && customer.address && customer.address.country && customer.address.country.id) || NONE_ID
                                        }
                                    >
                                        {this.state.countryId === NONE_ID && <option value={NONE_ID}>Select a country ...</option>}
                                        {configurationStore.countries
                                            .slice()
                                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} ({c.abbreviation})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="stateOrProvince">State or Province: </label>
                                    <select
                                        className="form-control not-required"
                                        name="stateOrProvince"
                                        disabled={!this.state.isEditable}
                                        onChange={this.onSelectProvince}
                                        value={
                                            this.state.isEditable
                                                ? this.state.provinceId
                                                : (customer && customer.address && customer.address.province && customer.address.province.id) ||
                                                  NONE_ID
                                        }
                                    >
                                        {this.state.countryId === NONE_ID && this.state.provinceId === NONE_ID && (
                                            <option value={NONE_ID}>Select a Province ...</option>
                                        )}
                                        {configurationStore.provinces
                                            .filter(p => !this.state.isEditable || p.countryId === this.state.countryId)
                                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} ({c.abbreviation})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div className="form-group col-md-4">
                                    <label htmlFor="primaryPhone">Primary Phone Number: </label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text">
                                                <FontAwesomeIcon icon={faPhone} />
                                            </div>
                                        </div>
                                        <input
                                            type="tel"
                                            name="primaryPhone"
                                            className="form-control not-required"
                                            pattern="^[a-zA-Z ]*$"
                                            placeholder={this.state.isEditable ? "" : "-"}
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.primaryPhone : customer.primaryPhone}
                                            readOnly={!this.state.isEditable}
                                        />
                                    </div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="secondaryPhone">Secondary Phone Number: </label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text">
                                                <FontAwesomeIcon icon={faPhone} />
                                            </div>
                                        </div>
                                        <input
                                            type="tel"
                                            name="secondaryPhone"
                                            placeholder={this.state.isEditable ? "" : "-"}
                                            className="form-control not-required"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.secondaryPhone : customer.secondaryPhone}
                                            readOnly={!this.state.isEditable}
                                        />
                                    </div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="faxNumber">Fax: </label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text">
                                                <FontAwesomeIcon icon={faPhone} />
                                            </div>
                                        </div>
                                        <input
                                            type="tel"
                                            name="faxNumber"
                                            placeholder={this.state.isEditable ? "" : "-"}
                                            className="form-control not-required"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.faxNumber : customer.faxNumber}
                                            readOnly={!this.state.isEditable}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div className="form-group col-md-4">
                                    <label htmlFor="email">Email: </label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text">@</div>
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder={this.state.isEditable ? "" : "-"}
                                            className="form-control not-required"
                                            onChange={this.handleOnChange}
                                            value={this.state.isEditable ? this.state.email : customer.email}
                                            readOnly={!this.state.isEditable}
                                        />
                                    </div>
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="formOfIdentification">Form of Identification: </label>
                                    <select
                                        className="form-control not-required"
                                        onChange={this.onSelectIdentification}
                                        name="formOfIdentification"
                                        value={
                                            this.state.isEditable
                                                ? this.state.identificationTypeId
                                                : customer && customer.identification.typeOfIdentification
                                                ? customer.identification.typeOfIdentification.id
                                                : NONE_ID
                                        }
                                        disabled={!this.state.isEditable}
                                    >
                                        <option value={NONE_ID}> - Unknown - </option>
                                        {configurationStore.identifiationTypes
                                            .filter(
                                                identificationType =>
                                                    identificationType.applicability === customer.type || identificationType.applicability === "BOTH"
                                            )
                                            .map(identificationType => (
                                                <option key={identificationType.id} value={identificationType.id}>
                                                    {identificationType.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="form-group col-md-4">
                                    <label htmlFor="identificationNumber">Identification #: </label>
                                    <input
                                        type="text"
                                        name="identificationNumber"
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        className="form-control not-required"
                                        onChange={this.handleOnChange}
                                        value={
                                            (this.state.isEditable
                                                ? this.state.identificationNumber
                                                : customer.identification.identificationNumber) || ""
                                        }
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                            </div>

                            <div className="form-row mb-3">
                                <div className="form-group col-md-4">
                                    <label htmlFor="notes">Aboriginal Status ID #: </label>
                                    <input
                                        type="text"
                                        name="firstNationsIdNumber"
                                        className="form-control not-required"
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        onChange={this.handleOnChange}
                                        value={this.state.isEditable ? this.state.firstNationsIdNumber : customer.firstNationsIdNumber || ""}
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div className="form-group col-md-12">
                                    <label htmlFor="notes">Notes: </label>
                                    <textarea
                                        name="notes"
                                        className="form-control not-required"
                                        rows={5}
                                        placeholder={this.state.isEditable ? "" : "-"}
                                        onChange={this.handleOnChange}
                                        value={this.state.isEditable ? this.state.notes || "" : customer.notes || ""}
                                        readOnly={!this.state.isEditable}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                    {this.state.isEditable && (
                        <div className="card-footer text-right">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm mr-2"
                                data-toggle="button"
                                aria-pressed="false"
                                onClick={this.cancelEditableFields}
                            >
                                {" "}
                                <FontAwesomeIcon icon={faBan} className="mr-2" />
                                Cancel
                            </button>
                            <button
                                form="customer-form"
                                type="button"
                                className="btn btn-primary btn-sm px-3"
                                data-toggle="button"
                                aria-pressed="false"
                                onClick={this.saveEditableFields}
                            >
                                {" "}
                                <FontAwesomeIcon icon={faSave} className="mr-2" />
                                Save
                            </button>
                        </div>
                    )}
                </div>
                {customer.type === "BUSINESS" && <BusinessContactRelationshipPanel />}
            </div>
        );
    }

    private copyCustomerToState(otherFields: Partial<State> = {}): Partial<State> {
        const customer = this.props.customerStore!.customer;
        if (!customer) {
            return {};
        }

        const updatedState: Partial<State> = Object.assign(
            {
                email: customer.email,
                faxNumber: customer.faxNumber,
                primaryPhone: customer.primaryPhone,
                secondaryPhone: customer.secondaryPhone,
                id: customer.id,
                firstNationsIdNumber: customer.firstNationsIdNumber,
                notes: customer.notes,
                type: customer.type,
                address: customer.address ? customer.address.address : "",
                countryId: (customer && customer.address && customer.address.country && customer.address.country.id) || NONE_ID,
                provinceId: (customer && customer.address && customer.address.province && customer.address.province.id) || NONE_ID,
                city: customer.address ? customer.address.city : "",
                postalCode: customer.address ? customer.address.postalCode : "",
                identificationTypeId:
                    (customer.identification && customer.identification.typeOfIdentification && customer.identification.typeOfIdentification.id) ||
                    NONE_ID,
                identificationNumber: customer.identification.identificationNumber || ""
            },
            otherFields
        );
        if (customer.type === "CONTACT") {
            updatedState.firstName = (customer as Contact).firstName;
            updatedState.middleName = (customer as Contact).middleName;
            updatedState.lastName = (customer as Contact).lastName;
        } else if (customer.type === "BUSINESS") {
            updatedState.businessName = (customer as Business).businessName;
        }

        return updatedState;
    }

    private buildFieldsToUpdate(): { [field: string]: string | number | null } {
        const customer = this.props.customerStore!.customer;
        if (!customer) {
            return {};
        }
        const obj: { [field: string]: string | number | null } = {};

        if (customer.address !== null) {
            if (customer.address.address !== this.state.address) {
                obj.address = this.state.address;
            }

            if (customer.address.postalCode !== this.state.postalCode) {
                obj.postalCode = this.state.postalCode;
            }

            if (customer.address.city !== this.state.city) {
                obj.city = this.state.city;
            }

            if (customer.address.province != null) {
                if (customer.address.province.id !== this.state.provinceId) {
                    obj.province = this.state.provinceId;
                }
            }

            if (customer.address.country != null) {
                if (customer.address.country.id !== this.state.countryId) {
                    obj.country = this.state.countryId;
                }
            }
        } else if (
            this.state.address !== "" ||
            this.state.postalCode !== "" ||
            this.state.city !== "" ||
            this.state.provinceId !== NONE_ID ||
            this.state.countryId !== NONE_ID
        ) {
            if (this.state.countryId !== NONE_ID) {
                obj.country = this.state.countryId;
            }

            if (this.state.provinceId !== NONE_ID) {
                obj.province = this.state.provinceId;
            }

            if (this.state.city !== "") {
                obj.city = this.state.city;
            }

            if (this.state.postalCode !== "") {
                obj.postalCode = this.state.postalCode;
            }

            if (this.state.address !== "") {
                obj.address = this.state.address;
            }
        }

        if (customer.notes !== this.state.notes) {
            obj.notes = this.state.notes;
        }

        if (customer.secondaryPhone !== this.state.secondaryPhone) {
            obj.secondaryPhone = this.state.secondaryPhone;
        }

        if (customer.primaryPhone !== this.state.primaryPhone) {
            obj.primaryPhone = this.state.primaryPhone;
        }

        if (customer.faxNumber !== this.state.faxNumber) {
            obj.faxNumber = this.state.faxNumber;
        }

        if (customer.email !== this.state.email) {
            obj.email = this.state.email;
        }

        if (customer.firstNationsIdNumber !== this.state.firstNationsIdNumber) {
            obj.firstNationsIdNumber = this.state.firstNationsIdNumber;
        }

        if (customer.identification) {
            if (this.state.identificationTypeId === NONE_ID && customer.identification.typeOfIdentification) {
                obj.typeOfIdentification = null;
            } else if (
                this.state.identificationTypeId !== NONE_ID &&
                (!customer.identification.typeOfIdentification || customer.identification.typeOfIdentification.id !== this.state.identificationTypeId)
            ) {
                obj.typeOfIdentification = this.state.identificationTypeId;
            }

            if (customer.identification.identificationNumber !== this.state.identificationNumber) {
                obj.identificationNumber = this.state.identificationNumber;
            }
        } else {
            if (this.state.identificationTypeId !== NONE_ID) {
                obj.typeOfIdentification = this.state.identificationTypeId;
            }
            if (this.state.identificationNumber !== "") {
                obj.identificationNumber = this.state.identificationNumber;
            }
        }

        if (customer.type === "CONTACT") {
            if ((customer as Contact).firstName !== this.state.firstName) {
                obj.firstName = this.state.firstName;
            }
            if ((customer as Contact).middleName !== this.state.middleName) {
                obj.middleName = this.state.middleName;
            }
            if ((customer as Contact).lastName !== this.state.lastName) {
                obj.lastName = this.state.lastName;
            }
        } else if (customer.type === "BUSINESS") {
            if ((customer as Business).businessName !== this.state.businessName) {
                obj.businessName = this.state.businessName;
            }
        }

        return obj;
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/BasicInformation.tsx